/* verqo — Buyer Landing (preview build) */
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SubPageLayout, Reveal, Eyebrow, CTABanner } from "../../../components/marketing/SubPageBlocks";
import { BUYER_NAV } from "../../../config/nav.buyer";

export default function BuyerLandingPreview() {
  return (
    <SubPageLayout modeOverride="buyer">
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="buyer-landing-hero">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <Eyebrow>Buyers · Retailers, chains, marketplaces</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 text-[40px] sm:text-[52px] md:text-[76px] leading-[1.0] tracking-tight font-semibold text-slate-900">
                One login.<br />
                Every supplier.<br />
                <span className="bg-lime-300 px-2.5 -rotate-1 inline-block my-1">Net-30 in seconds.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
                Get pre-approved once. Use your Verqo line at any supplier on the network. One AP process, one Trade Score, one login.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-9 flex flex-wrap gap-3 items-center">
                <a href="/waitlist?as=buyer" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity bg-[#0c1612]">
                  Request invite <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="/buyers/product/discover" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
                  Tour the product
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.36}>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Net-30/60/90 across the network</div>
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Buyer protection embedded</div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} y={32}>
            <div className="rounded-2xl p-8 md:p-10 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.4)] bg-[#0c1612]">
              <div className="text-[11px] uppercase tracking-[0.28em] text-lime-300 mb-6">Explore the buyer product</div>
              {BUYER_NAV.filter(t => t.mega).map((tab) => (
                <div key={tab.id} className="mb-6 last:mb-0">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50 font-semibold mb-2">{tab.label}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {tab.columns.flatMap(c => c.items).slice(0, 8).map(it => (
                      <Link
                        key={it.href}
                        to={it.href}
                        className="text-[11.5px] px-2.5 py-1 rounded-full bg-white/10 hover:bg-lime-300 hover:text-slate-900 text-white/85 transition-colors"
                        data-testid={`buyer-landing-chip-${it.slug}`}
                      >
                        {it.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-16 md:py-24 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="buyer-landing-sitemap">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500 font-semibold mb-6">
          Every page, one click away
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BUYER_NAV.filter(t => t.mega).flatMap(t => t.columns).map((col) => (
            <div key={col.heading} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">{col.heading}</div>
              <ul className="space-y-2.5">
                {col.items.map((it) => (
                  <li key={it.href}>
                    <Link to={it.href} className="group inline-flex items-center gap-1.5 text-[13.5px] text-slate-800 hover:text-lime-800 transition-colors">
                      {it.label}
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <CTABanner
        title="Terms across every supplier — one click."
        subtitle="Verqo Buyer is invite-only during launch. Join the list — we'll get you set up in under 48 hours."
        primary={{ label: "Request invite", href: "/waitlist?as=buyer" }}
        secondary={{ label: "Talk to us", href: "/company/contact" }}
      />
    </SubPageLayout>
  );
}
